import { memo } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import { OrderStatus } from "@/components/OrderControls";
import { ChevronDown, CheckCircle, Package, Ban, ListFilter, FileDown, Archive, ArchiveX, CalendarDays, Coins } from "lucide-react";
import { cn } from "@/lib/utils";

const statusConfig: Record<OrderStatus, { icon: React.ElementType; color: string }> = {
  "입금 완료": { icon: CheckCircle, color: "text-yellow-500" },
  "출고 완료": { icon: Package, color: "text-green-500" },
  "미지정": { icon: Ban, color: "text-gray-400" },
  "부분 입금": { icon: Coins, color: "text-amber-500" },
};

interface BatchActionToolbarProps {
  selectedCount: number;
  onBatchStatusChange: (status: OrderStatus) => void;
  onBatchDateChange: () => void;
  isProcessing: boolean;
  statusFilter: string;
  onStatusFilterChange: (status: string) => void;
  keepFilter: string;
  onKeepFilterChange: (status: string) => void;
  onDownloadExcel: () => void;
  // 스펙 D10: 계좌 연동(linked) 셀러만 '부분 입금'을 선택할 수 있다(Home의 selectableOrderStatuses 결과 주입).
  statusOptions: OrderStatus[];
}

function BatchActionToolbarImpl({
  selectedCount,
  onBatchStatusChange,
  onBatchDateChange,
  isProcessing,
  statusFilter,
  onStatusFilterChange,
  keepFilter,
  onKeepFilterChange,
  onDownloadExcel,
  statusOptions,
}: BatchActionToolbarProps) {
  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
      <div className="flex items-center gap-4">
        <div className="text-sm text-gray-600">
          <span className="font-semibold">{selectedCount}</span>개 항목 선택됨
        </div>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="flex items-center gap-2">
              <span>상태: {statusFilter === "all" ? "전체" : statusFilter}</span>
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuRadioGroup value={statusFilter} onValueChange={onStatusFilterChange}>
              <DropdownMenuRadioItem value="all">
                <ListFilter className="mr-2 h-4 w-4" />
                <span>전체</span>
              </DropdownMenuRadioItem>
              <DropdownMenuSeparator />
              {statusOptions.map(status => {
                const Icon = statusConfig[status]?.icon;
                const color = statusConfig[status]?.color;
                return (
                  <DropdownMenuRadioItem key={status} value={status}>
                    <Icon className={cn("mr-2 h-4 w-4", color)} />
                    <span>{status}</span>
                  </DropdownMenuRadioItem>
                );
              })}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="flex items-center gap-2">
              <span>
                킵: {keepFilter === 'all' ? '전체' : keepFilter === 'kept' ? '킵' : '킵 아닌 것'}
              </span>
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuRadioGroup value={keepFilter} onValueChange={onKeepFilterChange}>
              <DropdownMenuRadioItem value="all">
                <ListFilter className="mr-2 h-4 w-4" />
                <span>전체</span>
              </DropdownMenuRadioItem>
              <DropdownMenuSeparator />
              <DropdownMenuRadioItem value="kept">
                <Archive className="mr-2 h-4 w-4 text-orange-500" />
                <span>킵</span>
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="not-kept">
                <ArchiveX className="mr-2 h-4 w-4 text-gray-500" />
                <span>킵 아닌 것</span>
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          onClick={onDownloadExcel}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <FileDown className="mr-2 h-4 w-4" />
          엑셀 다운로드
        </Button>
        <Button
          variant="outline"
          onClick={onBatchDateChange}
          disabled={selectedCount === 0 || isProcessing}
          className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
        >
          <CalendarDays className="mr-2 h-4 w-4" />
          날짜 일괄 변경
        </Button>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button disabled={selectedCount === 0 || isProcessing}>
              {isProcessing ? "처리 중..." : "선택 항목 상태 변경"}
              <ChevronDown className="h-4 w-4 ml-2" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>변경할 상태 선택</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {statusOptions.includes('부분 입금') && (
              <DropdownMenuItem onSelect={() => onBatchStatusChange('부분 입금')}>
                <Coins className="mr-2 h-4 w-4 text-amber-500" />
                <span>부분 입금</span>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onSelect={() => onBatchStatusChange('입금 완료')}>
              <CheckCircle className="mr-2 h-4 w-4 text-yellow-500" />
              <span>입금 완료</span>
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onBatchStatusChange('출고 완료')}>
              <Package className="mr-2 h-4 w-4 text-green-500" />
              <span>출고 완료</span>
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onBatchStatusChange('미지정')}>
              <Ban className="mr-2 h-4 w-4 text-gray-400" />
              <span>미지정</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

export const BatchActionToolbar = memo(BatchActionToolbarImpl);